import React, { useEffect, useRef, useState } from 'react';
import { Card, Table } from 'react-bootstrap';
import { Pagination } from '../../../shared/components/Pagination';
import { BudgetOverviewService } from './BudgetOverviewService';
import { IBudget } from '../../statistics/dto/iBudget';
import { formatDate, getCorrectEnums } from '../../../utils/Functions';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { BudgetOverviewFilter } from './BudgetOverviewFilter';
import { calculateFee } from '../../../utils/helpers/FeeCaluculation';
import { ErrorToast } from '../../../utils/toasters';
import { EditableNumberCell } from '../../../shared/components/EditableNumberCell';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';

const BUDGET_COLUMNS: ITableColumn[] = [
    { label: 'Date of creation' },
    { label: 'Id' },
    { label: 'Campaign' },
    { label: 'Fee' },
    { label: 'Package' },
    { label: 'Company' },
    { label: 'Application deadline' },
    { label: 'Email' },
    { label: 'Planned budget' },
    { label: 'Total spent' },
    { label: 'Total invoiced' },
    { label: 'Difference' },
];
export function BudgetOverview() {
    const [budget, setBudget] = useState<IBudget[]>([]);
    const [totalPages, setTotalPages] = useState(0);
    const [campaignsFilter, setCampaignsFilter] = useState({
        page: 1,
        perPage: 15,
        id: '',
        from: undefined,
        to: undefined,
        hasSupervisor: true,
    });
    const [editingRow, setEditingRow] = useState<number | null>(null);
    const [editedValue, setEditedValue] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const requestIdRef = useRef(0);

    function getCampaigns(campaignsFilter: any, updatePage?: number) {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);

        BudgetOverviewService.getBudgetOverview({
            ...campaignsFilter,
            page: !!updatePage ? updatePage : campaignsFilter.page,
        })
            .then(data => {
                if (requestId !== requestIdRef.current) return;
                setBudget(data?.data);
                setCampaignsFilter({ ...campaignsFilter, page: data.page });
                setTotalPages(Math.ceil(data.count / campaignsFilter.perPage));
            })
            .catch(err => {
                if (requestId === requestIdRef.current) ErrorToast(err);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setIsLoading(false);
            });
    }

    useEffect(() => {
        getCampaigns(campaignsFilter, 1);
    }, [campaignsFilter?.id]);

    const handlePages = (updatePage: number) => {
        getCampaigns(campaignsFilter, updatePage);
    };

    return (
        <>
            <Card
                className="mb-5 p-4"
                onKeyDown={(e: any) => {
                    if (e.key === 'Enter') getCampaigns(campaignsFilter, 1);
                }}
            >
                <BudgetOverviewFilter
                    handleState={getCampaigns}
                    setState={setCampaignsFilter}
                    budgetFilter={campaignsFilter}
                />
            </Card>

            <Card className="mb-5 p-4 table-responsive">
                {isLoading ? (
                    <TableSkeleton columns={BUDGET_COLUMNS} rows={campaignsFilter.perPage} />
                ) : (
                    <Table hover>
                        <thead>
                            <tr>
                                {BUDGET_COLUMNS.map(column => (
                                    <th key={column.label} className={column.className}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {budget?.map(e => (
                                <tr key={e.id}>
                                    <td>{formatDate(e?.createdAt)}</td>
                                    <td>{e?.id}</td>
                                    <td>{e?.title}</td>
                                    <td>{e?.feePercentage ? calculateFee(e?.feePercentage) : ''}</td>
                                    <td>{getCorrectEnums(e?.packageName ?? '')}</td>
                                    <td>{getCorrectEnums(e?.companyName ?? '')}</td>
                                    <td>{formatDate(e?.date)}</td>
                                    <td>
                                        <ImpersonateEye id={e?.userId} />
                                        {e?.email}
                                    </td>
                                    <td className="text-right text-nowrap">{priceSplitter(e?.grossBudget ?? 0)} €</td>
                                    <td
                                        className={`${
                                            e?.grossBudget > e?.totalSpent ? 'text-danger' : 'text-primary'
                                        } text-right text-nowrap`}
                                    >
                                        {e?.isLongTerm
                                            ? priceSplitter(e?.totalSpentLong)
                                            : priceSplitter(e?.totalSpent)}{' '}
                                        €
                                    </td>
                                    <td>
                                        <EditableNumberCell
                                            value={e.totalInvoiced}
                                            onSave={newVal => {
                                                setBudget(prev =>
                                                    prev.map(item =>
                                                        item.id === e.id ? { ...item, totalInvoiced: newVal } : item
                                                    )
                                                );

                                                BudgetOverviewService.updateTotalInvoiced(e.id, {
                                                    totalInvoiced: newVal,
                                                }).catch(err => ErrorToast(err));
                                            }}
                                        />
                                    </td>
                                    <td className="text-right text-nowrap">
                                        {e?.isLongTerm
                                            ? priceSplitter(
                                                  e?.totalSpentLong ? e?.totalSpentLong - (e?.totalInvoiced ?? 0) : 0
                                              )
                                            : priceSplitter(
                                                  e?.totalSpent ? e?.totalSpent - (e?.totalInvoiced ?? 0) : 0
                                              )}
                                        €
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}

                <Pagination page={campaignsFilter.page} totalPages={totalPages} handlePagination={handlePages} />
            </Card>
        </>
    );
}
